import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, Lock, Sparkles, Mail } from "lucide-react";
import Aurora from "@/components/Aurora";
import { useAuth } from "@/contexts/AuthContext";
import { getCheckoutStatus, formatApiErrorDetail } from "@/lib/api";

export default function Signup() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { user, signup } = useAuth();

  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    let mounted = true;
    async function verify() {
      if (!sessionId) {
        setVerifying(false);
        setErr("Missing checkout session.");
        return;
      }
      try {
        const resp = await getCheckoutStatus(sessionId);
        if (!mounted) return;
        if (resp.payment_status === "paid" && !resp.account_created) {
          setVerified(true);
          if (resp.customer_email) setEmail(resp.customer_email);
        } else if (resp.account_created) {
          setErr("An account was already created for this purchase. Please log in.");
        } else {
          setErr("Payment is not confirmed yet for this session.");
        }
      } catch (e) {
        setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      } finally {
        if (mounted) setVerifying(false);
      }
    }
    verify();
    return () => { mounted = false; };
  }, [sessionId]);

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await signup({ session_id: sessionId, password });
      navigate("/dashboard", { replace: true });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden grid place-items-center px-5 py-16">
      <Aurora variant="intense" rings />
      <div className="relative max-w-md w-full">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <span className="inline-flex w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 grid place-items-center shadow-[0_0_24px_rgba(0,229,255,0.5)]">
            <Sparkles className="w-4 h-4 text-black" strokeWidth={2.5} />
          </span>
          <span className="font-display text-white text-lg">
            <span className="font-normal">Favor</span>{" "}
            <span className="font-bold">Studio.</span>
          </span>
        </Link>

        <div className="glass-card p-7 sm:p-9">
          <div className="label-chip"><span className="dot" /><span>STEP 2 OF 2 &middot; CREATE ACCOUNT</span></div>
          <h1 className="font-display text-3xl sm:text-4xl mt-5">
            <span className="font-normal">Set Your </span>
            <span className="font-bold">Password.</span>
          </h1>
          <p className="mt-3 text-zinc-400 text-sm leading-relaxed">
            Your payment was confirmed. Choose a password to unlock the dashboard.
          </p>

          {verifying ? (
            <div className="mt-7 flex items-center gap-3 text-zinc-400">
              <Loader2 className="w-4 h-4 animate-spin text-cyan-300" />
              <span className="text-sm">Verifying your purchase…</span>
            </div>
          ) : !verified ? (
            <div className="mt-7 space-y-4">
              <div className="flex items-start gap-3 text-rose-300 text-sm">
                <Lock className="w-4 h-4 mt-0.5 shrink-0" />
                <span data-testid="signup-error-message">{err || "This session can't be used to sign up."}</span>
              </div>
              <Link to="/login" className="btn-ghost inline-flex">Go to log in</Link>
              <div className="text-xs text-zinc-500">
                Need help? <a className="text-cyan-300 hover:underline" href="mailto:contact@favorstudio.agency">contact@favorstudio.agency</a>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="signup-form">
              <div>
                <label className="text-[11px] tracking-[0.25em] uppercase text-zinc-500">Email</label>
                <div className="mt-1.5 relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    data-testid="signup-email-input"
                    type="email"
                    required
                    readOnly
                    value={email}
                    title="This is the email Stripe verified for your payment and can't be changed here."
                    className="w-full bg-transparent border border-white/10 rounded-full pl-11 pr-5 py-3 text-white placeholder:text-zinc-500 text-sm cursor-not-allowed opacity-80"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] tracking-[0.25em] uppercase text-zinc-500">Name (optional)</label>
                <input
                  data-testid="signup-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 w-full bg-transparent border border-white/10 focus:border-cyan-400/60 focus:outline-none rounded-full px-5 py-3 text-white placeholder:text-zinc-500 text-sm transition-colors"
                />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.25em] uppercase text-zinc-500">Password</label>
                <input
                  data-testid="signup-password-input"
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="mt-1.5 w-full bg-transparent border border-white/10 focus:border-cyan-400/60 focus:outline-none rounded-full px-5 py-3 text-white placeholder:text-zinc-500 text-sm transition-colors"
                />
              </div>
              {err && (
                <p data-testid="signup-error-message" className="text-rose-300 text-sm">{err}</p>
              )}
              <button
                data-testid="signup-submit-button"
                type="submit"
                disabled={busy}
                className="btn-aurora w-full disabled:opacity-70"
              >
                {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> CREATING ACCOUNT…</>) : (<>UNLOCK MY COURSE <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 text-center">
                One account &middot; Lifetime access
              </p>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-zinc-500 mt-6">
          Already have an account? <Link to="/login" className="text-cyan-300 hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
}
